import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { DetalhamentoImportacaoResponse } from 'src/models/interfaces/importação/detalhamento-importacao-interface';
import { ResumoImportacao } from 'src/models/interfaces/importação/resumo-importacao-interface';
import { StatusImportacao } from 'src/models/interfaces/importação/status-importacao-interface';
import {
  ImportacaoResponse,
  UltimaImportacaoResponse,
  UploadImportacao,
  ValidaCabecalho
} from 'src/models/interfaces/importação/upload-importacao-interface';
import { HeadersService } from '../header.service';
import { LOADING_INSTANCE } from 'src/app/interceptor/network.interceptor';
import { ImportServiceV3LoadingKey } from 'src/models/keys/imports/imports.keys';
import { Nullable } from 'src/models/types/utility-types';

@Injectable({
  providedIn: 'root'
})
export class ImportServiceV3 {
  private readonly url = `${environment.apiUrl}/api/v3/importacoes`;

  constructor(
    private readonly http: HttpClient,
    private headersService: HeadersService
  ) {
    this.headersService.includeObject({ fileName: 'import.service-v3.ts', url: this.url });
  }

  public getUltimaImportacao(
    skipLoading: Nullable<ImportServiceV3LoadingKey> = null
  ): Observable<UltimaImportacaoResponse> {
    return this.http.get<UltimaImportacaoResponse>(`${this.url}/ultima`, {
      context: new HttpContext().set(LOADING_INSTANCE, skipLoading)
    });
  }

  public getLinhasComErros(
    idImportacao: number,
    page: number = 0,
    size: number = 1,
    skipLoading: Nullable<ImportServiceV3LoadingKey>
  ): Observable<ImportacaoResponse> {
    return this.http.get<ImportacaoResponse>(`${this.url}/${idImportacao}/linhas/erros`, {
      params: { page: page.toString(), size: '9999' },
      context: new HttpContext().set(LOADING_INSTANCE, skipLoading)
    });
  }

  public upload(file: File, skipLoading: Nullable<ImportServiceV3LoadingKey> = null): Observable<UploadImportacao> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<UploadImportacao>(`${this.url}/upload`, formData, {
      context: new HttpContext().set(LOADING_INSTANCE, skipLoading)
    });
  }

  public validarCabecalho(idImportacao: string): Observable<ValidaCabecalho> {
    return this.http.post<ValidaCabecalho>(`${this.url}/${idImportacao}/cabecalho/valida`, {});
  }

  public corrigirCabecalho(
    idImportacao: string,
    itens: { nomeCabecalhoOficial: string; nomeCabecalhoArquivo: string }[],
    skipLoading: Nullable<ImportServiceV3LoadingKey> = null
  ): Observable<any> {
    return this.http.put<any>(
      `${this.url}/${idImportacao}/cabecalho`,
      { itens },
      {
        context: new HttpContext().set(LOADING_INSTANCE, skipLoading)
      }
    );
  }

  public corrigirLinhas(
    idImportacao: string,
    linhasErro: { idLinha: number; mapLinha: Record<string, any> }[],
    skipLoading: Nullable<ImportServiceV3LoadingKey> = null
  ): Observable<any> {
    return this.http.put<any>(
      `${this.url}/${idImportacao}/linhas`,
      { linhasErro },
      {
        context: new HttpContext().set(LOADING_INSTANCE, skipLoading)
      }
    );
  }

  public getDetalhamento(
    idImportacao: string,
    skipLoading: Nullable<ImportServiceV3LoadingKey> = null
  ): Observable<DetalhamentoImportacaoResponse> {
    return this.http.get<DetalhamentoImportacaoResponse>(`${this.url}/${idImportacao}/detalhamento`, {
      context: new HttpContext().set(LOADING_INSTANCE, skipLoading)
    });
  }

  public cancelarUltimaImportacao(skipLoading: Nullable<ImportServiceV3LoadingKey> = null): Observable<any> {
    return this.http.post<any>(
      `${this.url}/cancela-ultima-importacao`,
      {},
      {
        context: new HttpContext().set(LOADING_INSTANCE, skipLoading)
      }
    );
  }
}
